import {
  ApolloClient,
  CombinedGraphQLErrors,
  InMemoryCache,
  type FieldPolicy,
} from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';
import { ApolloProvider } from '@apollo/client/react';
import { useAuth0 } from '@auth0/auth0-react';
import { type ReactNode, useMemo } from 'react';
import { apolloTypePolicies } from 'wingman-fe';

import { useEnvironmentConfig } from './environment.tsx';
import { useStaticRender } from './staticRender.tsx';

import {
  useDatadog,
  type DatadogContext,
} from '#src/components/Datadog/Datadog.tsx';
import { useTracingHeaders } from '#src/hooks/tracingHeaders.tsx';
import { possibleTypes } from '#src/types/possibleTypes.json' with { type: 'json' };

const dashboardTypePolicies = () => {
  const conservativePaginationKeyArgs = ['after', 'before', 'first', 'last'];
  const typePolicies = apolloTypePolicies({
    paginationPolicy: 'infinite-scroll',
  });

  // Apply conservative type policy for BrandSelect pagination
  // TODO: consider upstreaming this to Wingman as a configuration/preset

  typePolicies.AdvertisementBrandingsConnection = {};

  const advertisementBrandingsQueryPolicy = typePolicies.Query?.fields
    ?.advertisementBrandings as FieldPolicy | undefined;

  if (!advertisementBrandingsQueryPolicy) {
    throw new Error(
      'Missing Query.fields.advertisementBrandings in apolloTypePolicies',
    );
  }

  advertisementBrandingsQueryPolicy.keyArgs = [
    'filter',
    'hirerId',
    ...conservativePaginationKeyArgs,
  ];

  return typePolicies;
};

interface ApolloEnvironmentProviderProps {
  children: ReactNode;
}

declare module '@apollo/client/react' {
  export interface DefaultContext {
    headers?: Record<string, string>;
    appContext?: AppContext;
    /**
     * Controls whether only critical errors should be tracked by Datadog.
     */
    criticalErrorsOnlyTracking?: boolean;
  }
}

interface AppContext {
  baseUrl: string;
  getToken: () => Promise<string>;
  tracingHeaders: Record<string, string>;
}

const fakeFetch = () =>
  Promise.resolve({
    status: 200,
    text: () => Promise.resolve(JSON.stringify({ data: null })),
  });

const httpLink = new HttpLink({
  fetch: globalThis.fetch ?? fakeFetch,
  uri: (operation) => {
    const { appContext } = operation.getContext();

    if (!appContext) {
      throw new Error('App context is missing');
    }

    return `${appContext.baseUrl}/graphql`;
  },
});

const authLink = new SetContextLink(async (context) => {
  const { headers, appContext } = context;

  if (!appContext) {
    throw new Error('App context is missing');
  }

  const token = await appContext.getToken();
  return {
    ...context,
    headers: {
      ...appContext.tracingHeaders,
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  };
});

const errorLink = (datadog: DatadogContext) =>
  new ErrorLink(({ error, operation }) => {
    const errors = CombinedGraphQLErrors.is(error) ? error.errors : [error];
    if (!errors.length) {
      return;
    }

    const { criticalErrorsOnlyTracking } = operation.getContext();

    if (
      !criticalErrorsOnlyTracking ||
      !errors.every((err) => {
        const code = 'extensions' in err ? err.extensions?.code : err.message;

        return (
          typeof code === 'string' &&
          ['FORBIDDEN', 'BAD_USER_INPUT'].includes(code)
        );
      })
    ) {
      datadog.addError(new Error(errors[0].message), { error });
    }
  });

export const ApolloEnvironmentProvider = ({
  children,
}: ApolloEnvironmentProviderProps) => {
  const { baseUrls } = useEnvironmentConfig();
  const staticRender = useStaticRender();
  const auth = useAuth0();
  const tracingHeaders = useTracingHeaders();
  const datadog = useDatadog();
  const client = useMemo(() => {
    const apolloClient = new ApolloClient({
      cache: new InMemoryCache({
        possibleTypes,
        typePolicies: dashboardTypePolicies(),
      }),
      ssrMode: staticRender,
      defaultOptions: {
        query: { errorPolicy: 'all' },
        watchQuery: { errorPolicy: 'all' },
        mutate: { errorPolicy: 'all' },
      },
      link: authLink.concat(errorLink(datadog)).concat(httpLink),
      defaultContext: {
        appContext: {
          baseUrl: baseUrls.graphql,
          getToken: auth.getAccessTokenSilently,
          tracingHeaders,
        },
      },
    });

    return apolloClient;
  }, [auth, baseUrls.graphql, staticRender, tracingHeaders, datadog]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
